import * as React from 'react';
import { ConfigurationService } from '../../../config/configuration-service';
import { LoadingIndicator, Sizes } from './loading-indicators';

export enum ErrorTypes {
  BadRequest = 400,
  Unauthorized = 401,
  Forbidden = 403,
  NotFound = 404,
  ServerError = 500,
  BadGateway = 502,
  ServiceUnavaliable = 503
}

interface IErrorHandlerOwnProps {
  children: any;
  error: Error;
  errorInfo?: React.ErrorInfo;
  onRetryClick?: (event: React.MouseEvent<HTMLElement>) => void;
  ignoreErrors?: ErrorTypes[];
}

interface IErrorHandlerState {
  debugMode: boolean;
}

interface IErrorOwnProps {
  message: string;
  description?: React.ReactNode;
  error: Error;
  errorInfo?: React.ErrorInfo;
  onClickAction: (event: React.MouseEvent<HTMLElement>) => void;
}

const CommonError = ({ onClickAction, message, description }: IErrorOwnProps) => (
  <div className="jumbotron alert alert-danger" style={{ borderRadius: 0, padding: 15 }}>
    <h2 style={{ margin: 0 }}>{message}</h2>
    <p style={{ marginBottom: 10 }}>{description}</p>
    <button onClick={onClickAction} className="btn btn-xs btn-danger">Click Here</button> to retry.
  </div>
);

const CommonDebugError = ({ message, description, error, errorInfo, onClickAction }: IErrorOwnProps) => (
  <div className="jumbotron alert alert-danger" style={{ borderRadius: 0, padding: 15 }}>
    <h2 style={{ margin: 0 }}>{message} Please see details below:</h2>
    <p>{description}</p>
    <h3>Error:</h3>
    <p style={{ marginLeft: 20, marginBottom: 10 }} dangerouslySetInnerHTML={{ __html: error.stack.trim().replace(/\n/g, '<br />') }} />
    {getDebugStack(errorInfo)}
    <button onClick={onClickAction} className="btn btn-xs btn-danger">Click Here</button> to retry.
  </div>
);

const getErrorCondition = (err) => {
  if (err.response && err.response.status) {
    return err.response.status;
  }

  return err.message;
};

const getDebugStack = (errorInfo: React.ErrorInfo) => {
  if (errorInfo) {
    return (
      <React.Fragment>
        <h3>React Component Stack:</h3>
        <p style={{ marginLeft: 20 }} dangerouslySetInnerHTML={{ __html: errorInfo.componentStack.trim().replace(/\n/g, '<br />') }} />
      </React.Fragment>
    );
  }

  return '';
};

export class ErrorHandler extends React.Component<IErrorHandlerOwnProps, IErrorHandlerState> {
  constructor(props) {
    super(props);

    this.state = {
      debugMode: false
    };
  }

  async componentDidMount() {
    const { debugMode } = await ConfigurationService.getConfiguration();

    this.setState({ debugMode });
  }

  reloadPage = (event: React.MouseEvent<HTMLElement>) => {
    if (this.props.onRetryClick) {
      this.props.onRetryClick(event);
    } else {
      console.warn('WARNING: Error handler reload button action not attached!');
    }
  }

  displayError = () => {
    const { errorInfo, error, ignoreErrors } = this.props;

    const params = { error, errorInfo, onClickAction: this.reloadPage };
    const errorCondition = getErrorCondition(error);

    if (ignoreErrors && ignoreErrors.findIndex(e => e === errorCondition) >= 0) {
      return this.props.children;
    }

    if (this.state.debugMode) {
      return <CommonDebugError {...params} message="Debug" />;
    }

    switch (errorCondition) {
      case ErrorTypes.BadRequest:
        return <CommonError {...params} message="400 - Bad Request" />;

      case ErrorTypes.Unauthorized:
        return <LoadingIndicator size={Sizes.Medium} />;

      case ErrorTypes.Forbidden:
        return <CommonError {...params} message="403 - Forbidden" />;

      case ErrorTypes.NotFound:
        return <CommonError {...params} message="404 - Not Found" />;

      case ErrorTypes.ServerError:
      case ErrorTypes.BadGateway:
      case ErrorTypes.ServiceUnavaliable:
        return <CommonError {...params} message="500 - Server Error" />;

      default:
        return <CommonError {...params} message="An Unknown Error Has Occured" />;
    }
  }

  render() {
    if (this.props.error) {
      return this.displayError();
    }

    return this.props.children;
  }
}
